import React, { Fragment, useCallback, useEffect } from 'react';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import {
  MeQuery,
  MeWithIdentities,
  UserAnalyticsFunnelData,
} from '../../../../graphql/queries/account';
import {
  identitiesFilteredOnAccess,
  sortByIdentityRelationship,
} from '../../../../util/identity';
import { getPurchasedIdentitiesOnFolder } from '../../../../util/account';
import { AlbumFolderWithIdentityAccess } from '../../../../graphql/queries/album-folder';
import WaldoSpeechBubble from '../../../../components/global/WaldoSpeechBubble';
import OnboardingLayoutFooter from '../../../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import Button from '../../../../components/global/Button';
import Spinner from '../../../../components/global/Spinner';
import JoinAlbumFolderIdentitiesSelection from './JoinAlbumFolderIdentitiesSelection';
import { checkoutAlbumFolder } from '../../../../actions/discover-album-folder';
import { getAlbumFolderCheckoutItems } from '../../../../util/discover-album-folder';
import { albumFolderRequiresPhotoSearchOnboarding } from '../../../../util/album-folder';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import { joinByCode as joinByCodeMutation } from '../../../../graphql/mutations/albums';
import { albumFolderJoin as albumFolderJoinMutation } from '../../../../graphql/mutations/album-folder';
import Alert from '../../../../components/global/Alert';
import useOpenAlert from '../../../../hooks/useOpenAlert';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  loading: PropTypes.bool,
  identities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  checkout: PropTypes.func.isRequired,
  joinByCode: PropTypes.func.isRequired,
  albumFolderJoin: PropTypes.func.isRequired,
};

const JoinAlbumFolder = ({
  user,
  albumFolder,
  loading,
  identities,
  checkout,
  joinByCode,
  albumFolderJoin,
}) => {
  const history = useHistory();
  const {
    selectedIdentities,
    success,
    error,
    loading: loadingCheckout,
  } = useSelector(({ discoverAlbumFolder }) => ({
    selectedIdentities: discoverAlbumFolder.identities,
    loading: discoverAlbumFolder.loading,
    error: discoverAlbumFolder.error,
    success: discoverAlbumFolder.success,
  }));
  const dispatch = useDispatch();
  const onAlbumFolderCheckout = useCallback(
    (...args) => dispatch(checkoutAlbumFolder(...args)),
    [dispatch],
  );
  const openAlert = useOpenAlert();
  const hasIdentities = (identities?.length ?? 0) > 0;

  useEffect(() => {
    if (success) {
      openAlert({
        type: 'success',
        title: 'Congrats!',
        message: `${pluralize(
          'face',
          selectedIdentities.length,
          true,
        )} has been joined to the event!`,
      });

      history.replace(
        `/app/shutterbug/hierarchy-items/${albumFolder.id}/matches`,
      );
    }
  }, [success]);

  if (loading || !albumFolder) {
    return <Spinner />;
  }

  const hasSearchSubscription =
    (user?.search_subscriptions?.records?.length ?? 0) > 0;

  const subscriptionRequired =
    albumFolderRequiresPhotoSearchOnboarding(albumFolder) &&
    !hasSearchSubscription;

  const onCheckout = (stripeToken = {}) => {
    const identityIds = selectedIdentities.map(({ id }) => id);

    if (subscriptionRequired) {
      history.push(
        `/join/album-folder/${albumFolder.id}/photo-search-checkout`,
      );

      return;
    }

    onAlbumFolderCheckout({
      accountId: user.id,
      email: stripeToken.email,
      stripeToken: stripeToken.id,
      stripePaymentMethodId: stripeToken.stripe_payment_method_id,
      stripeConnectAccountId: stripeToken.stripe_connect_account_id,
      purchaseItems: {
        ...getAlbumFolderCheckoutItems(selectedIdentities),
        promoCode: stripeToken.promo_code,
      },
      identityIdsToJoin: identityIds,
      albumFolderJoinCode: albumFolder.join_code,
      albumFolderId: albumFolder.id,
      checkoutMutation: checkout,
      joinByCodeMutation: joinByCode,
      albumFolderJoinMutation: albumFolderJoin,
    });
  };

  const submitBtnLabel = (() => {
    if (loadingCheckout) {
      return 'Processing...';
    }

    return subscriptionRequired ? 'Subscribe' : 'Done';
  })();

  return (
    <Fragment>
      <WaldoSpeechBubble position="right" className="mb-4">
        Tell me who you want me to look for!
      </WaldoSpeechBubble>
      <JoinAlbumFolderIdentitiesSelection />

      {((hasIdentities && selectedIdentities.length > 0) || error) && (
        <OnboardingLayoutFooter>
          {error && <Alert type="danger">{error}</Alert>}

          {hasIdentities && (
            <Fragment>
              <Button
                onClick={onCheckout}
                btnBlock
                className="mt-3"
                disabled={selectedIdentities.length === 0 || loadingCheckout}
              >
                {submitBtnLabel}
              </Button>
              <Button
                onClick={() => history.goBack()}
                btnBlock
                btnStyle="link"
                className="mt-1 text-center text-bold"
              >
                Go Back
              </Button>
            </Fragment>
          )}
        </OnboardingLayoutFooter>
      )}
    </Fragment>
  );
};

JoinAlbumFolder.propTypes = propTypes;

const withUserData = graphql(MeWithIdentities, {
  props: ({ data: { me, loading } = {}, ownProps }) => ({
    loading,
    accountId: me?.id,
    identities: sortByIdentityRelationship(
      identitiesFilteredOnAccess(
        me?.linked_identities?.records ?? [],
        ownProps?.albumFolder?.album_identity_access,
      ),
    ),
    purchasedIdentities: getPurchasedIdentitiesOnFolder(
      me,
      ownProps?.albumFolder?.root?.id,
    ),
    user: me,
  }),
});

const withAlbumFolder = graphql(AlbumFolderWithIdentityAccess, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumFolderId,
    },
    notifyOnNetworkStatusChange: true,
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    albumFolder,
    loading,
  }),
});

const withCheckout = graphql(checkoutMutation, {
  props: ({ mutate, ownProps }) => ({
    checkout: ({
      accountId,
      email,
      stripeToken,
      ppaItems,
      customPrintItems,
      searchSubscriptionItems,
      promoCode,
      stripePaymentMethodId,
      stripeConnectAccountId,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            email_address: email,
            stripe_token: stripeToken,
            subscription_items: ppaItems,
            custom_print_items: customPrintItems,
            search_subscription_items: searchSubscriptionItems,
            promo_code: promoCode,
            platform: 'WEB',
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
          },
        },
        refetchQueries: [
          {
            query: MeQuery,
          },
          {
            query: MeWithIdentities,
          },
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const withJoinByCode = graphql(joinByCodeMutation, {
  props: ({ mutate, ownProps }) => ({
    joinByCode: (accountId, joinCode, identities, albumFolderJoin) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            join_code: joinCode,
            identities,
            album_folder_join: albumFolderJoin,
          },
        },
        refetchQueries: [
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

const withAlbumFolderJoin = graphql(albumFolderJoinMutation, {
  props: ({ mutate, ownProps }) => ({
    albumFolderJoin: ({ albumFolderId, identities }) =>
      mutate({
        variables: {
          input: {
            album_folder_id: albumFolderId,
            identity_ids: identities,
          },
        },
        refetchQueries: [
          {
            query: UserAnalyticsFunnelData,
            variables: {
              albumFolderId: ownProps.match.params.albumFolderId,
            },
          },
        ],
      }),
  }),
});

export default compose(
  withRouter,
  withAlbumFolder,
  withUserData,
  withCheckout,
  withJoinByCode,
  withAlbumFolderJoin,
)(JoinAlbumFolder);
